import { type UseMutationOptions, useMutation } from '@tanstack/react-query';
import type { CreateBenchmarkBody } from '../../generated';
import { createBenchmarkV1WorkspacesWorkspaceIdBenchmarksPost } from '../../generated';
import { type BenchmarkView, toBenchmarkView } from '../../queries/search-benchmarks';

/*
 * POST /v1/workspaces/{workspace_id}/benchmarks: creates a custom benchmark and answers with it, narrowed like the
 * list's by `toBenchmarkView`. Tests answer this path with the MSW handler in `tests/mocks/handlers.ts`.
 */

export type CreateBenchmarkMutationVariables = { workspace_id: string } & CreateBenchmarkBody;

export type CreateBenchmarkMutation = BenchmarkView;

async function createBenchmark({ workspace_id, ...body }: CreateBenchmarkMutationVariables) {
  const { data } = await createBenchmarkV1WorkspacesWorkspaceIdBenchmarksPost({
    path: { workspace_id },
    body,
    throwOnError: true,
  });
  return toBenchmarkView(data);
}

export const useCreateBenchmarkMutation = <TError = unknown, TContext = unknown>(
  options?: Omit<
    UseMutationOptions<CreateBenchmarkMutation, TError, CreateBenchmarkMutationVariables, TContext>,
    'mutationKey' | 'mutationFn'
  >,
) => {
  return useMutation<CreateBenchmarkMutation, TError, CreateBenchmarkMutationVariables, TContext>({
    mutationKey: ['CreateBenchmark'],
    mutationFn: createBenchmark,
    ...options,
  });
};

useCreateBenchmarkMutation.fetcher = (variables: CreateBenchmarkMutationVariables) => () => createBenchmark(variables);
